/**
 * Product Documents Widget
 *
 * Manages PDF/document attachments on the Item Detail page.
 * Pattern: mirrors ProductGalleryWidget (fetch → list → upload → save → reorder → delete).
 * API: zaviago_backend.api.item_documents (get/save/add)
 */

import { useState, useMemo, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { ShopifySectionWidgetProps } from '../types';
import { useLanguage } from '@/i18n/useLanguage';
import { getUITranslation } from '@/lib/views/labels/translations';
import {
  FileText,
  Plus,
  Trash2,
  GripVertical,
  Download,
  Eye,
  EyeOff,
  X,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getQueryOptions } from '@/lib/config/queryConfig';
import { getERPClient } from '@/lib/erp/client';
import { AttachmentPickerDialog } from '@/components/attachments';

// ── Types ────────────────────────────────────────────────────────────────────

interface DocumentItem {
  name: string;
  file: string;
  title: string;
  document_type: string;
  file_size: number;
  is_public: number;
  idx: number;
}

interface DocumentsResponse {
  item_code: string;
  documents: DocumentItem[];
  schema_version: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const DOCUMENT_QUERY_KEY = 'item-documents';

function formatFileSize(bytes: number): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileExtension(url: string): string {
  const parts = url.split('/').pop()?.split('?')[0]?.split('.') ?? [];
  return parts.length > 1 ? parts.pop()!.toUpperCase() : '';
}

const DOC_TYPE_COLORS: Record<string, string> = {
  Datasheet: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  Manual: 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300',
  Certificate: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  Warranty: 'bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
  Other: 'bg-gray-50 text-gray-600 dark:bg-gray-900 dark:text-gray-400',
};

// ── Component ────────────────────────────────────────────────────────────────

export function ProductDocumentsWidget({
  formData,
  disabled,
}: ShopifySectionWidgetProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<Set<string>>(new Set());
  const { language } = useLanguage();

  const itemCode = formData.item_code;
  const queryClient = useQueryClient();

  const labels = useMemo(() => {
    const t = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      title: t('documents_title', 'PRODUCT DOCUMENTS'),
      addDocuments: t('documents_add', 'Add Documents'),
      noDocuments: t('documents_empty_title', 'No documents'),
      noDocumentsHint: t('documents_empty_hint', 'Add datasheets, manuals, or certificates'),
      remove: t('documents_remove', 'Remove'),
      cancel: t('cancel', 'Cancel'),
      delete: t('delete', 'Delete'),
      removing: t('removing', 'Removing...'),
      selectToRemove: t('documents_select_to_remove', 'Select documents to remove'),
    };
  }, [language]);

  // ── Fetch documents ────────────────────────────────────────────────────────

  const { data: docTypes } = useQuery({
    queryKey: ['item-document-types'],
    queryFn: async () => {
      return await getERPClient().call<string[]>(
        'zaviago_backend.api.item_documents.get_document_types',
      );
    },
    ...getQueryOptions('detail'),
    staleTime: 5 * 60 * 1000, // 5 min — types rarely change
  });

  const documentTypeOptions = docTypes ?? ['Datasheet', 'Manual', 'Certificate', 'Warranty', 'Other'];

  const { data: docsData } = useQuery({
    queryKey: [DOCUMENT_QUERY_KEY, itemCode],
    queryFn: async () => {
      if (!itemCode) return null;
      return await getERPClient().call<DocumentsResponse>(
        'zaviago_backend.api.item_documents.get_item_documents',
        { item_code: itemCode },
      );
    },
    enabled: !!itemCode,
    ...getQueryOptions('detail'),
  });

  const documents = useMemo(() => docsData?.documents ?? [], [docsData]);

  // ── Save mutation (full replace) ───────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: async (docs: DocumentItem[]) => {
      if (!itemCode) throw new Error('Item code is required');
      return await getERPClient().call<DocumentsResponse>(
        'zaviago_backend.api.item_documents.save_item_documents',
        {
          item_code: itemCode,
          documents_json: JSON.stringify(
            docs.map((d, i) => ({
              name: d.name,
              file: d.file,
              title: d.title,
              document_type: d.document_type,
              file_size: d.file_size,
              is_public: d.is_public,
              idx: i + 1,
            })),
          ),
        },
      );
    },
    onSettled: () => {
      if (!itemCode) return;
      queryClient.invalidateQueries({ queryKey: [DOCUMENT_QUERY_KEY, itemCode] });
    },
  });

  // ── Add from attachment picker ─────────────────────────────────────────────

  const addMutation = useMutation({
    mutationFn: async (fileUrls: string[]) => {
      if (!itemCode) throw new Error('Item code is required');
      return await getERPClient().call<DocumentsResponse>(
        'zaviago_backend.api.item_documents.add_documents_from_files',
        {
          item_code: itemCode,
          files_json: JSON.stringify(fileUrls.map((url) => ({ file: url }))),
        },
      );
    },
    onSettled: () => {
      if (!itemCode) return;
      queryClient.invalidateQueries({ queryKey: [DOCUMENT_QUERY_KEY, itemCode] });
    },
  });

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleAttachmentComplete = useCallback(
    (result?: { attachments?: Array<{ file_url?: string }> }) => {
      setIsAddDialogOpen(false);
      if (!result?.attachments?.length) return;
      const urls = result.attachments
        .map((a) => a.file_url)
        .filter((u): u is string => !!u);
      if (urls.length > 0) {
        addMutation.mutate(urls);
      }
    },
    [addMutation],
  );

  const handleChangeDocumentType = useCallback(
    (doc: DocumentItem, newType: string) => {
      const updated = documents.map((d) =>
        d.name === doc.name ? { ...d, document_type: newType } : d,
      );
      saveMutation.mutate(updated);
    },
    [documents, saveMutation],
  );

  const handleToggleVisibility = useCallback(
    (doc: DocumentItem) => {
      const updated = documents.map((d) =>
        d.name === doc.name ? { ...d, is_public: d.is_public ? 0 : 1 } : d,
      );
      saveMutation.mutate(updated);
    },
    [documents, saveMutation],
  );

  const handleDeleteSelected = useCallback(() => {
    if (selectedForDelete.size === 0) return;
    const remaining = documents.filter((d) => !selectedForDelete.has(d.name));
    saveMutation.mutate(remaining);
    setSelectedForDelete(new Set());
    setIsDeleteMode(false);
  }, [documents, selectedForDelete, saveMutation]);

  const toggleSelectForDelete = useCallback((name: string) => {
    setSelectedForDelete((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  // ── Attachment context ─────────────────────────────────────────────────────

  const attachContext = useMemo(
    () => ({ doctype: 'Item' as const, name: itemCode }),
    [itemCode],
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  const isEmpty = documents.length === 0;
  const isBusy = saveMutation.isPending || addMutation.isPending;

  return (
    <Card className="overflow-hidden border-0 shadow-none bg-transparent">
      {/* Header */}
      <div className="flex items-center justify-between px-1 pb-3">
        <h3 className="text-xs font-semibold tracking-wide text-muted-foreground">
          {labels.title}
        </h3>
        <div className="flex items-center gap-1">
          {documents.length > 0 && !disabled && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-destructive hover:text-destructive"
              onClick={() => {
                if (isDeleteMode) {
                  setIsDeleteMode(false);
                  setSelectedForDelete(new Set());
                } else {
                  setIsDeleteMode(true);
                }
              }}
            >
              {isDeleteMode ? (
                <>
                  <X className="mr-1 h-3 w-3" />
                  {labels.cancel}
                </>
              ) : (
                <>
                  <Trash2 className="mr-1 h-3 w-3" />
                  {labels.remove}
                </>
              )}
            </Button>
          )}
          {!disabled && !isDeleteMode && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setIsAddDialogOpen(true)}
              disabled={isBusy || documents.length >= 10}
            >
              <Plus className="mr-1 h-3 w-3" />
              {labels.addDocuments}
            </Button>
          )}
        </div>
      </div>

      <CardContent className="p-0">
        {/* Empty state */}
        {isEmpty && !isBusy && (
          <button
            type="button"
            onClick={() => !disabled && setIsAddDialogOpen(true)}
            disabled={disabled}
            className={cn(
              'flex w-full flex-col items-center justify-center gap-2 rounded border-2 border-dashed p-8 transition-colors',
              disabled
                ? 'cursor-not-allowed border-muted/30 text-muted-foreground/50'
                : 'cursor-pointer border-muted-foreground/20 text-muted-foreground hover:border-primary/40 hover:bg-accent/30',
            )}
          >
            <FileText className="h-8 w-8 opacity-40" />
            <span className="text-sm font-medium">{labels.noDocuments}</span>
            <span className="text-xs opacity-60">{labels.noDocumentsHint}</span>
          </button>
        )}

        {/* Document list */}
        {!isEmpty && (
          <div className="divide-y divide-border rounded border bg-card shadow-sm">
            {documents.map((doc) => {
              const ext = getFileExtension(doc.file);
              const size = formatFileSize(doc.file_size);
              const typeColor = DOC_TYPE_COLORS[doc.document_type] ?? DOC_TYPE_COLORS.Other;
              const isSelected = selectedForDelete.has(doc.name);

              return (
                <div
                  key={doc.name}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 transition-colors',
                    isDeleteMode && 'cursor-pointer',
                    isSelected && 'bg-destructive/5',
                  )}
                  onClick={isDeleteMode ? () => toggleSelectForDelete(doc.name) : undefined}
                >
                  {/* Delete checkbox */}
                  {isDeleteMode && (
                    <div
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-md border',
                        isSelected
                          ? 'border-destructive bg-destructive text-destructive-foreground'
                          : 'border-muted-foreground/30',
                      )}
                    >
                      {isSelected && (
                        <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none">
                          <path d="M3 6L5.5 8.5L9 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  )}

                  {/* Drag handle (placeholder for future DnD) */}
                  {!isDeleteMode && !disabled && (
                    <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/30" />
                  )}

                  {/* File icon + extension badge */}
                  <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-muted/50">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    {ext && (
                      <span className="absolute -bottom-0.5 -right-0.5 rounded-md px-0.5 text-[8px] font-bold leading-tight text-muted-foreground">
                        {ext}
                      </span>
                    )}
                  </div>

                  {/* Title + meta */}
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium">{doc.title || doc.file.split('/').pop()}</span>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {disabled ? (
                        <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-medium', typeColor)}>
                          {doc.document_type}
                        </span>
                      ) : (
                        <Select
                          value={doc.document_type}
                          onValueChange={(val) => handleChangeDocumentType(doc, val)}
                        >
                          <SelectTrigger
                            className={cn(
                              'h-5 w-auto min-w-0 gap-1 rounded-full border-0 px-1.5 py-0 text-[10px] font-medium shadow-none',
                              typeColor,
                            )}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {documentTypeOptions.map((opt) => (
                              <SelectItem key={opt} value={opt} className="text-xs">
                                {opt}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {size && <span>{size}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  {!isDeleteMode && (
                    <div className="flex shrink-0 items-center gap-1">
                      {!disabled && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleVisibility(doc);
                          }}
                          title={doc.is_public ? 'Public — visible on storefront' : 'Private — not visible on storefront'}
                        >
                          {doc.is_public ? (
                            <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                          ) : (
                            <EyeOff className="h-3.5 w-3.5 text-muted-foreground/50" />
                          )}
                        </Button>
                      )}
                      <a
                        href={doc.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-xs hover:bg-accent"
                        title="Download"
                      >
                        <Download className="h-3.5 w-3.5 text-muted-foreground" />
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Delete confirmation bar */}
        {isDeleteMode && selectedForDelete.size > 0 && (
          <div className="mt-3 flex items-center justify-between rounded-sm bg-destructive/10 px-3 py-2">
            <span className="text-sm text-destructive">
              {selectedForDelete.size} {labels.selectToRemove}
            </span>
            <Button
              variant="destructive"
              size="sm"
              className="h-7 text-xs"
              onClick={handleDeleteSelected}
              disabled={isBusy}
            >
              {isBusy ? labels.removing : labels.delete}
            </Button>
          </div>
        )}
      </CardContent>

      {/* Attachment picker dialog */}
      {itemCode && (
        <AttachmentPickerDialog
          open={isAddDialogOpen}
          onOpenChange={setIsAddDialogOpen}
          context={attachContext}
          multiple
          accept={[
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          ]}
          mode="upload-only"
          onCompleted={handleAttachmentComplete}
        />
      )}
    </Card>
  );
}
